//src/actions/chatAction.ts
import { ActionTypes } from "../store/actionTypes";
import ChatApi, {
    type CreateUserThreadOptions,
    type GetUserThreadsOptions,
    type HandleMessageOptions,
} from "../apis/chatApi";

const chatApi = new ChatApi();

export const ChatAction = {
    getTicketThread: (ticketId: string) => async (dispatch: any) => {
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
        try {
            const response = await chatApi.getTicketThread(ticketId);
            dispatch({
                type: ActionTypes.GET_TICKET_THREAD,
                payload: {
                    ...response,
                    ai_thread: {
                        ...response?.ai_thread,
                        // Always stamp the requested ticket so the pane can
                        // tell this thread is loaded (proto ticket_id may be a
                        // string or { value }).
                        ticket_id: { value: ticketId }
                    }
                }
            });
            return response;
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    createTicketThread: (ticketId: string) => async (dispatch: any) => {
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
        try {
            const response = await chatApi.createTicketThread(ticketId);
            dispatch({
                type: ActionTypes.CREATE_TICKET_THREAD,
                payload: {
                    ...response,
                    ticket_id: { value: ticketId }
                }
            });
            return response;
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    handleMessage: (
        aiThreadId: string,
        userId: string,
        content: string,
        structuredWorkflow?: Record<string, unknown>,
        requestId?: string,
        options?: HandleMessageOptions
    ) => async (dispatch: any) => {
        try {
            const response = await chatApi.handleMessage(
                aiThreadId,
                userId,
                content,
                structuredWorkflow,
                requestId,
                options
            );
            return response;
        } catch (error: any) {
            throw error;
        }
    },

    cancelTurn: (aiThreadId: string) => async () => {
        await chatApi.cancelTurn(aiThreadId);
    },

    handleHumanReview: (aiThreadId: string, userId: string, action: string, data?: string, messageId?: string) => async (dispatch: any, getState: any) => {
        try {
            await chatApi.handleHumanReview(aiThreadId, userId, action, data, messageId);
            if ((action === "CONTINUE" || action === "UPDATE") && data && messageId) {
                try {
                    const parsed = JSON.parse(data);
                    const userEdits = parsed.userEdits;
                    if (userEdits && typeof userEdits === "object" && Object.keys(userEdits).length > 0) {
                        dispatch({
                            type: ActionTypes.MERGE_HITL_EDITED_ARGS,
                            payload: { messageId, userEdits },
                        });
                    }
                } catch {
                    // ignore parse errors for merge
                }
            }
        } catch (error) {
            throw error;
        }
    },

    deleteAiThread: (aiThreadId: string) => async (dispatch: any) => {
        try {
            await chatApi.deleteAiThread(aiThreadId);
            const numericThreadId = Number(aiThreadId);
            dispatch({
                type: ActionTypes.DELETE_USER_THREAD,
                payload: Number.isFinite(numericThreadId) ? numericThreadId : aiThreadId,
            });
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    resetAiThread: (aiThreadId: string, ticketId: string) => async (dispatch: any) => {
        try {
            await chatApi.deleteAiThread(aiThreadId);
            dispatch({ type: ActionTypes.CLEAR_CHAT });
            // Now create a new thread
            const response = await chatApi.createTicketThread(ticketId);
            dispatch({
                type: ActionTypes.CREATE_TICKET_THREAD,
                payload: {
                    ...response,
                    ticket_id: { value: ticketId }
                }
            });
            return response; // so the component can use the new thread id if needed
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    clearMessages: () => (dispatch: any) => {
        dispatch({ type: ActionTypes.CLEAR_MESSAGES });
    },

    getUserThread: (userId: string) => async (dispatch: any) => {
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
        try {
            const response = await chatApi.getUserThread(userId);
            dispatch({
                type: ActionTypes.GET_USER_THREAD,
                payload: response
            });
            return response;
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    createUserThread: (
        userId: string,
        customerId: number | null = null,
        options?: CreateUserThreadOptions
    ) => async (dispatch: any) => {
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
        try {
            const response = await chatApi.createUserThread(userId, customerId, options);
            dispatch({
                type: ActionTypes.CREATE_USER_THREAD,
                payload: response
            });
            return response;
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    fetchUserThreads: (userId: string, options?: GetUserThreadsOptions) => async (dispatch: any) => {
        dispatch({ type: ActionTypes.FETCH_USER_THREADS_REQUEST });
        try {
            const response = await chatApi.getUserThreads(userId, options);
            dispatch({
                type: ActionTypes.FETCH_USER_THREADS_SUCCESS,
                payload: response
            });
            return response;
        } catch (error) {
            dispatch({
                type: ActionTypes.FETCH_USER_THREADS_FAILURE,
                payload: error
            });
            throw error;
        }
    },

    setSelectedUserThreadId: (threadId: string) => (dispatch: any) => {
        dispatch({
            type: ActionTypes.SET_SELECTED_USER_THREAD_ID,
            payload: threadId
        });
    },

    renameUserThread: (aiThreadId: string, newTitle: string) => async (dispatch: any) => {
        try {
            await chatApi.renameUserThread(aiThreadId, newTitle);
            dispatch({
                type: ActionTypes.RENAME_USER_THREAD,
                payload: { aiThreadId, newTitle }
            });
            return { success: true };
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    updateThreadPinned: (aiThreadId: string, pinned: boolean) => async (dispatch: any, getState: any) => {
        try {
            await chatApi.updateAiThreadPinned(aiThreadId, pinned);
            // Only fetch latest threads for up-to-date info
            const userEmail = getState().auth.user?.email;
            if (userEmail) {
                await dispatch(ChatAction.fetchUserThreads(userEmail));
            }
        } catch (error) {
            dispatch({ type: ActionTypes.CHAT_ERROR, payload: error });
            throw error;
        }
    },

    updateThreadShared: (aiThreadId: string, shared: boolean) => async (dispatch: any) => {
        try {
            const response: any = await chatApi.updateAiThreadShared(aiThreadId, shared);
            if (response?.error || response?.success === false) {
                throw response;
            }
            dispatch({
                type: ActionTypes.UPDATE_THREAD_SHARED,
                payload: { ai_thread_id: aiThreadId, shared }
            });
            return { success: true };
        } catch (error) {
            dispatch({
                type: ActionTypes.CHAT_ERROR,
                payload: error
            });
            throw error;
        }
    },

    upsertUserThread: (thread: any) => (dispatch: any) => {
        dispatch({
            type: ActionTypes.UPSERT_USER_THREAD,
            payload: thread
        });
    },

    // Detail fetch for one thread: full messages plus per-message enrichment.
    // The thread list is metadata-only, so the selected thread's messages load
    // incrementally here instead of blocking the whole list.
    fetchThreadDetail: (aiThreadId: string) => async (dispatch: any) => {
        const response: any = await chatApi.getAiThreadById(aiThreadId);
        const aiThread = response?.ai_thread;
        if (!aiThread?.ai_thread_id) {
            throw new Error("Thread not found");
        }
        dispatch({
            type: ActionTypes.UPSERT_USER_THREAD,
            payload: aiThread
        });
        return aiThread;
    },
};
